import {
  API_TYPES,
  type CredentialProvisionableQualification,
  type CredentialProvisionRequestType,
  PostWorkerCredentialProvisionRequestRequestSchema,
  type PostWorkerCredentialProvisionRequestResponse,
  PostWorkerCredentialProvisionRequestResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { post } from "../../api/parsedApi";
import { environmentConfig } from "../../environment";
import { type ResponseWithError } from "../../ExperimentalHomeHealth/Cases/types";
import { logError } from "../../lib/analytics";
import { APP_V2_APP_EVENTS } from "../../lib/analytics/events";
import { showErrorToast } from "../../lib/Notifications";
import { buildGetWorkerCredentialProvisionRequestsUrl } from "./useGetWorkerCredentialProvisionRequests";

export function getWorkerCredentialRequestEndpointUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-credential-provision-requests`;
}

interface CreateWorkerCredentialRequestParams {
  workerId: string;
  workplaceId: string;
  qualification: CredentialProvisionableQualification;
  requestType: CredentialProvisionRequestType;
}

export function useCreateWorkerCredentialProvisionRequest(
  options: UseMutationOptions<
    PostWorkerCredentialProvisionRequestResponse,
    unknown,
    CreateWorkerCredentialRequestParams
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workerId, workplaceId, qualification, requestType }) => {
      const response = await post({
        url: getWorkerCredentialRequestEndpointUrl(workplaceId),
        data: {
          data: {
            type: API_TYPES.workerCredentialProvisionRequest,
            attributes: {
              requestType,
              qualification,
            },
            relationships: {
              worker: {
                data: {
                  type: "worker",
                  id: workerId,
                },
              },
            },
          },
        },
        bodySchema: PostWorkerCredentialProvisionRequestRequestSchema,
        responseSchema: PostWorkerCredentialProvisionRequestResponseSchema,
      });

      return response.data;
    },
    onSuccess: (data, variables, context) => {
      const { workplaceId, workerId } = variables;
      void queryClient.invalidateQueries({
        queryKey: [buildGetWorkerCredentialProvisionRequestsUrl(workplaceId, workerId)],
      });
      options.onSuccess?.(data, variables, context);
    },
    onError: (error: AxiosError<ResponseWithError>, params) => {
      logError(APP_V2_APP_EVENTS.CREATE_WORKER_CREDENTIAL_PROVISION_REQUEST_FAILURE, {
        error,
        metadata: {
          ...params,
          errorMessage: error.response?.data?.errors?.[0]?.detail,
        },
      });
      showErrorToast(
        error.response?.data?.errors?.[0]?.detail ?? "Failed to submit credential request"
      );
    },
    meta: {
      logSuccessMessage: APP_V2_APP_EVENTS.CREATE_WORKER_CREDENTIAL_PROVISION_REQUEST_SUCCESS,
      userSuccessMessage: "Credential request submitted successfully",
    },
    ...options,
  });
}
